import { memo, useLayoutEffect, useRef, useState } from "react";
import { StarIcon } from "lucide-react";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";
import { type Icon, ProviderLogo } from "./providerIconUtils";

/**
 * A single company entry in the picker's left rail. Each becomes one brand
 * icon button keyed by the company `id`; clicking it filters the main list to
 * that company's catalog group(s).
 */
export type ModelPickerRailCompany = {
  id: string;
  displayName: string;
  icon: Icon;
  /**
   * Simple Icons slug for the real brand logo. When present the rail renders
   * the CDN logo, falling back to `icon` (the monogram/brand SVG) if it 404s.
   */
  simpleIcon?: string | undefined;
  /**
   * When true this is a "coming soon" provider: it renders dimmed +
   * non-interactive with a "<Name> — coming soon" tooltip and does nothing
   * on click. Active companies (false/undefined) stay click-to-filter.
   */
  comingSoon?: boolean;
};

/** The rail selection: either the Favorites pseudo-entry or a company id. */
export type ModelPickerRailSelection = string | "favorites";

const SELECTED_INDICATOR_CLASS =
  "pointer-events-none absolute -right-1 top-1/2 z-10 h-5 w-0.75 -translate-y-1/2 rounded-l-full bg-primary";

/** Opens toward the rail so the list stays readable (not over the model names). */
const PICKER_TOOLTIP_SIDE = "left" as const;
const PICKER_TOOLTIP_SIDE_OFFSET = 8;
const PICKER_TOOLTIP_CLASS = "max-w-64 text-balance font-normal leading-snug";

const RAIL_BUTTON_CLASS =
  "relative isolate flex w-full cursor-pointer aspect-square items-center justify-center rounded-md transition-colors hover:bg-[color-mix(in_srgb,var(--popover)_90%,var(--foreground))] focus-visible:bg-[color-mix(in_srgb,var(--popover)_90%,var(--foreground))] focus-visible:outline-none";

export const ModelPickerSidebar = memo(function ModelPickerSidebar(props: {
  /** Currently selected rail entry — "favorites" or a company id. */
  selectedId: ModelPickerRailSelection;
  onSelect: (id: ModelPickerRailSelection) => void;
  /**
   * Provider companies to render, one brand icon per company (already deduped
   * across platforms by the caller). These replace the old per-driver-instance
   * rail — no raw coding-agent driver icons appear here anymore.
   */
  companies: ReadonlyArray<ModelPickerRailCompany>;
  /** Render the favorites rail entry at the top. */
  showFavorites?: boolean;
}) {
  const showFavorites = props.showFavorites ?? true;
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const [selectedIndicatorTop, setSelectedIndicatorTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    const content = sidebarContentRef.current;
    if (!content) {
      return;
    }
    const selectedItem = Array.from(
      content.querySelectorAll<HTMLElement>("[data-model-picker-provider]"),
    ).find((item) => item.dataset.modelPickerProvider === props.selectedId);
    if (!selectedItem) {
      setSelectedIndicatorTop(null);
      return;
    }
    setSelectedIndicatorTop(selectedItem.offsetTop + selectedItem.offsetHeight / 2 - 10);
  }, [props.companies, props.selectedId, showFavorites]);

  return (
    <div className="w-11 shrink-0 overflow-hidden bg-muted/30" data-model-picker-sidebar="true">
      <div className="h-full overflow-y-auto overscroll-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div ref={sidebarContentRef} className="relative flex min-h-full flex-col gap-1 p-1">
          {selectedIndicatorTop !== null ? (
            <div
              data-model-picker-selected-indicator="true"
              className={cn(
                SELECTED_INDICATOR_CLASS,
                "right-0 translate-y-0 transition-[top] duration-200 ease-out",
              )}
              style={{ top: selectedIndicatorTop }}
            />
          ) : null}
          {/* Favorites section */}
          {showFavorites ? (
            <>
              <div className="relative w-full" data-model-picker-provider="favorites">
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        className={RAIL_BUTTON_CLASS}
                        onClick={() => props.onSelect("favorites")}
                        type="button"
                        aria-label="Favorites"
                      >
                        <StarIcon className="size-5 fill-current shrink-0" aria-hidden />
                      </button>
                    }
                  />
                  <TooltipPopup
                    side={PICKER_TOOLTIP_SIDE}
                    sideOffset={PICKER_TOOLTIP_SIDE_OFFSET}
                    align="center"
                    className={PICKER_TOOLTIP_CLASS}
                  >
                    Favorites
                  </TooltipPopup>
                </Tooltip>
              </div>
              <div className="border-b border-border/70" aria-hidden="true" />
            </>
          ) : null}

          {/* Company buttons — one brand icon per provider company. Active
              companies filter the list; coming-soon ones render dimmed and do
              nothing on click. */}
          {props.companies.map((company) => {
            const isComingSoon = company.comingSoon === true;
            return (
              <div
                key={company.id}
                className="relative w-full"
                data-model-picker-provider={company.id}
              >
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        className={cn(
                          RAIL_BUTTON_CLASS,
                          isComingSoon &&
                            "cursor-default opacity-40 hover:bg-transparent focus-visible:bg-transparent",
                        )}
                        onClick={isComingSoon ? undefined : () => props.onSelect(company.id)}
                        type="button"
                        aria-label={
                          isComingSoon
                            ? `${company.displayName} — coming soon`
                            : company.displayName
                        }
                        aria-disabled={isComingSoon || undefined}
                      >
                        <ProviderLogo
                          slug={company.simpleIcon}
                          name={company.displayName}
                          fallback={company.icon}
                          className="size-5"
                        />
                        {isComingSoon ? (
                          <span
                            className="pointer-events-none absolute -bottom-0.5 -right-0.5 rounded-sm bg-muted px-0.5 text-[6px] font-bold uppercase leading-tight tracking-tight text-muted-foreground shadow-sm"
                            aria-hidden
                          >
                            soon
                          </span>
                        ) : null}
                      </button>
                    }
                  />
                  <TooltipPopup
                    side={PICKER_TOOLTIP_SIDE}
                    sideOffset={PICKER_TOOLTIP_SIDE_OFFSET}
                    align="center"
                    className={PICKER_TOOLTIP_CLASS}
                  >
                    {isComingSoon ? `${company.displayName} — coming soon` : company.displayName}
                  </TooltipPopup>
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});
